import React from "react";
import { useTranslation } from "react-i18next";

interface BookingCalendarProps {
  currentMonth: number;
  currentYear: number;
  today: Date;
  onPrevMonth: () => void;
  onNextMonth: () => void;
  onSelectDate: (dateStr: string) => void;
  closedDates?: string[];
}

export const BookingCalendar: React.FC<BookingCalendarProps> = ({
  currentMonth,
  currentYear,
  today,
  onPrevMonth,
  onNextMonth,
  onSelectDate,
  closedDates = [],
}) => {
  const { i18n, t } = useTranslation("common");
  const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
  const firstDayIndex = new Date(currentYear, currentMonth, 1).getDay();

  // En: 1. Define closed days of the week (0 = Sunday, 6 = Saturday) if needed
  // JS Day Index: Sunday = 0, Monday = 1, Tuesday = 2 ..., Saturday = 6
  // Exemple: If the restaurant is closed on Sundays and Mondays, you would set: CLOSED_DAYS_OF_WEEK = [0, 1];
  // If closed Wednesdays: CLOSED_DAYS_OF_WEEK = [3];
  // If works every day, then CLOSED_DAYS_OF_WEEK = [];
  const CLOSED_DAYS_OF_WEEK: number[] = [];

  const weekDays = [
    t("calendar.days.sun", "Nd"),
    t("calendar.days.mon", "Pn"),
    t("calendar.days.tue", "Wt"),
    t("calendar.days.wed", "Śr"),
    t("calendar.days.thu", "Cz"),
    t("calendar.days.fri", "Pt"),
    t("calendar.days.sat", "Sob"),
  ];

  return (
    <div className="p-4 sm:p-6 space-y-6">
      {/* Calendar Month Header */}
      <div className="flex items-center justify-between bg-white dark:bg-stone-900 p-4 rounded-2xl border border-stone-200 dark:border-amber-500/20 shadow-sm">
        <button
          onClick={onPrevMonth}
          disabled={
            currentMonth === today.getMonth() &&
            currentYear === today.getFullYear()
          }
          className="text-amber-600 dark:text-amber-400 hover:text-amber-500 disabled:opacity-30 transition px-3 py-1 font-serif text-xl"
        >
          ←
        </button>
        <span className="font-serif text-base sm:text-lg font-bold tracking-wider text-stone-900 dark:text-stone-100 capitalize">
          {new Date(currentYear, currentMonth).toLocaleDateString(
            i18n.language,
            {
              month: "long",
              year: "numeric",
            }
          )}
        </span>
        <button
          onClick={onNextMonth}
          className="text-amber-600 dark:text-amber-400 hover:text-amber-500 transition px-3 py-1 font-serif text-xl"
        >
          →
        </button>
      </div>

      {/* Grid */}
      <div className="grid grid-cols-7 gap-2 text-center">
        {weekDays.map((day, idx) => (
          <span
            key={idx}
            className="text-[10px] font-serif font-bold text-amber-700 dark:text-amber-400 uppercase tracking-widest pb-2"
          >
            {day}
          </span>
        ))}

        {Array.from({ length: firstDayIndex }).map((_, i) => (
          <div key={`empty-${i}`} />
        ))}

        {Array.from({ length: daysInMonth }).map((_, i) => {
          const dayNum = i + 1;
          const dateObj = new Date(currentYear, currentMonth, dayNum);
          const startOfToday = new Date(
            today.getFullYear(),
            today.getMonth(),
            today.getDate()
          );

          const yearStr = currentYear;
          const monthStr = String(currentMonth + 1).padStart(2, "0");
          const dayStr = String(dayNum).padStart(2, "0");
          const dateString = `${yearStr}-${monthStr}-${dayStr}`;

          const isPast = dateObj < startOfToday;
          const dayOfWeek = dateObj.getDay();
          const isClosedDay = CLOSED_DAYS_OF_WEEK.includes(dayOfWeek);
          const isSpecificClosedDate = closedDates.includes(dateString);
          const isDisabled = isPast || isClosedDay || isSpecificClosedDate;

          return (
            <button
              key={dayNum}
              disabled={isDisabled}
              onClick={() => onSelectDate(dateString)}
              title={
                isClosedDay || isSpecificClosedDate
                  ? t(
                      "calendar.closedDayNote",
                      "Restauracja jest zamknięta w tym dniu"
                    )
                  : undefined
              }
              className={`h-10 sm:h-11 rounded-xl text-xs sm:text-sm font-serif font-semibold transition-all duration-200 flex items-center justify-center ${
                isPast
                  ? "text-stone-400 dark:text-stone-700 cursor-not-allowed bg-stone-100 dark:bg-stone-900/30 line-through opacity-60"
                  : "bg-white dark:bg-stone-900 hover:bg-amber-600 hover:text-white dark:hover:bg-amber-600 text-stone-800 dark:text-stone-200 border border-stone-200 dark:border-stone-800 hover:border-amber-500 hover:shadow-lg hover:shadow-amber-600/20"
              }`}
            >
              {dayNum}
            </button>
          );
        })}
      </div>
    </div>
  );
};
